import { Concept } from '../types';

export const concepts: Concept[] = [
  {
    id: "premium-enterprise",
    name: "1. The Premium Enterprise Trust",
    tagline: "High-Volume Scaling with Bank-Grade Predictability",
    aesthetic: "Ultra-premium, heavy corporate, clean and trustworthy. Emphasizes enterprise security, predictable business rules, and flawless operational scalability.",
    colors: {
      bg: "bg-slate-50/70",
      text: "text-slate-900",
      border: "border-slate-250/80",
      accent: "indigo",
      accentBg: "bg-indigo-600",
      accentText: "text-indigo-650",
      gradient: "from-indigo-50 via-slate-50 to-blue-50/50"
    },
    fontFamily: "sans",
    heroHeadlinePrefix: "Scale your customer operations on",
    typingWords: ["WhatsApp Business.", "Live Web Chat.", "Instagram Direct."],
    heroCTA: "Accelerate Operations Now",
    visualMockups: [
      {
        platform: "whatsapp",
        title: "WhatsApp Business Enterprise",
        userQuery: "Hi, our logistics firm needs to reschedule shipment #9948A. Can we move it to Thursday morning?",
        botResponse: "Certainly, Mr. Vance. I've accessed your corporate portal. Delivery #9948A is updated to Thursday, June 4th at 9:00 AM. A calendar confirmation has been sent to your registered address.",
        outcomeBadge: "Rescheduled in 6.4 seconds",
        avatarUrl: "🏢",
        timestamp: "10:32 AM"
      },
      {
        platform: "webchat",
        title: "Secure Enterprise Web Chat",
        userQuery: "We need an export of our service logs for the audited billing cycle of Q1.",
        botResponse: "Logs compiled under SOC2 guidelines. I've sent a secure, tokenized download link directly to your verified email. It will expire in 15 minutes.",
        outcomeBadge: "Audited & Dispatched",
        avatarUrl: "🛡️",
        timestamp: "10:33 AM"
      }
    ],
    beforeVsAfter: {
      beforeLabel: "Chaotic Manual Queues",
      afterLabel: "Automated Enterprise Grid",
      beforeIcon: "AlertTriangle",
      afterIcon: "ShieldCheck",
      beforeText: [
        "Uncapped hold times causing 18% support abandonment rates.",
        "Overwhelmed staff struggling with repetitive, easily automated queries.",
        "Human agents misquoting service policies or delivery timetables."
      ],
      afterText: [
        "92% of standard inquiries fully resolved within 8 seconds.",
        "Strict operational boundaries ensure the AI never misrepresents policies.",
        "Agents are freed up to focus entirely on high-value, complex contract negotiations."
      ]
    },
    industryShowcase: [
      {
        industry: "Hospitality",
        subTitle: "Enterprise Guest Relations",
        workflowTitle: "VIP Arrival Automation",
        workflowSteps: [
          "Guest books hotel suite via WhatsApp Business",
          "CAEL AI reads loyalty membership status instantly",
          "Automated room upgrade requested & digital key delivered on-screen",
          "Local dining recommendations personal concierge dispatched via text"
        ],
        valueMetric: "NPS increased by 28 points",
        channelUsed: "WhatsApp Business Integration",
        highlightText: "Deliver five-star white-glove treatment to thousands of international arrivals simultaneously."
      },
      {
        industry: "Retail",
        subTitle: "Omni-Channel Retail Logistics",
        workflowTitle: "Instant Automated Exchanges",
        workflowSteps: [
          "Buyer sends a picture of a damaged retail item on Instagram DM",
          "CAEL AI automatically verifies order history & checks inventory real-time",
          "Return QR code and prepaid shipping label dispatched within seconds",
          "Trigger replacement dispatch to local logistics center"
        ],
        valueMetric: "Return overhead slashed by 64%",
        channelUsed: "Instagram DM Integration",
        highlightText: "Turn product returns and complaints into smooth, automated micro-transactions that build brand loyalty."
      },
      {
        industry: "Fintech",
        subTitle: "Regulated Financial Operations",
        workflowTitle: "Frictionless Account Audits",
        workflowSteps: [
          "Customer asks for active interest statements on secure Web Chat",
          "Two-factor authentication code sent via WhatsApp & validated by CAEL AI",
          "Annual statement requested, rendered as PDF and pushed to browser",
          "Automated session termination and encryption cleanup executed"
        ],
        valueMetric: "94% lower compliance-risk audit costs",
        channelUsed: "Secure SDK Web Chat",
        highlightText: "Enable instant self-service account queries without taking security or compliance risks."
      }
    ],
    emotionalHook: "Give your enterprise the operational peace of mind of an automated worker that handles high volumes, respects policy guidelines, and never takes a day off.",
    targetAudience: "COOs, VPs of Customer Operations, and Executive Directors searching for scalable support solutions.",
    businessOutcome: "90%+ resolution rates that cut monthly queue overhead by up to 55% while elevating service speed."
  },
  {
    id: "ecommerce-specialist",
    name: "2. The High-Growth E-Commerce Specialist",
    tagline: "Convert DMs into Sales and Eradicate Cart Abandonment",
    aesthetic: "Modern, lively, highly visual B2B conversion layout. Striking mint and soft emerald palette designed to appeal directly to DTC, retail brand owners, and Shopify merchants.",
    colors: {
      bg: "bg-stone-50/70",
      text: "text-slate-900",
      border: "border-stone-200",
      accent: "emerald",
      accentBg: "bg-emerald-500",
      accentText: "text-emerald-700",
      gradient: "from-emerald-50 via-stone-50 to-teal-50"
    },
    fontFamily: "outfit",
    heroHeadlinePrefix: "Turn conversations into checkouts on",
    typingWords: ["Instagram Direct.", "WhatsApp Commerce.", "Web Storefront."],
    heroCTA: "Multiply Digital Conversions",
    visualMockups: [
      {
        platform: "instagram",
        title: "Instagram DM Social Commerce",
        userQuery: "Hello! I saw your linen jumpsuit ad, do you have size M available in Sage Green?",
        botResponse: "Yes, we do! 🌿 I've reserved a Medium Sage Jumpsuit for you. Would you like to check out now? I can apply our exclusive 15% VIP discount code right here.",
        outcomeBadge: "Checkout link generated",
        avatarUrl: "🛍️",
        timestamp: "Just now"
      },
      {
        platform: "whatsapp",
        title: "WhatsApp Abandoned Cart Recoverer",
        userQuery: "Wait, can I add the matching leather sandals to my checkout order?",
        botResponse: "Added! Absolute perfection. Your new total with the sandals and jumpsuit in size M is $134. Tap the secure link below to complete your order instantly.",
        outcomeBadge: "Cart recovered successfully",
        avatarUrl: "👜",
        timestamp: "1 min ago"
      }
    ],
    beforeVsAfter: {
      beforeLabel: "Leaking Social Leads",
      afterLabel: "High-Velocity Checkout Automation",
      beforeIcon: "AlertOctagon",
      afterIcon: "TrendingUp",
      beforeText: [
        "Shoppers drop off because custom sizing inquiries take days to answer via email.",
        "Social DMs sit neglected for over 12 hours, losing customer intent and enthusiasm.",
        "Cart abandonment emails are marked as spam and read rates continue to plummet."
      ],
      afterText: [
        "Unbelievably quick, interactive sizing and product stock answers, directly inside chat.",
        "DMs engaged instantly 24/7, with customized discount triggers driven by high purchasing intent.",
        "Over 35% of abandoned items are recovered through direct, frictionless conversational checkouts."
      ]
    },
    industryShowcase: [
      {
        industry: "Retail",
        subTitle: "Direct-to-Consumer Growth",
        workflowTitle: "DM-to-Doorstep Checkout",
        workflowSteps: [
          "Shopper comments on an IG post about a new denim launch",
          "CAEL AI instantly fires an automated, warm welcome DM with stock details",
          "Customer selects wash and size from rich in-app media cards",
          "Unified Stripe checkout payment completed inside chat"
        ],
        valueMetric: "Conversion rates up 3.4x",
        channelUsed: "Instagram DM Direct Flow",
        highlightText: "Unlock the direct viral path from social media attention to transactional capture in under 60 seconds."
      }
    ],
    emotionalHook: "Stop losing sales to slow response times. CAEL AI engages eager shoppers, guides sizing decisions, and closes checkouts directly in the DM within seconds.",
    targetAudience: "DTC Founders, E-commerce Directors, and Growth Marketers seeking to maximize average order value.",
    businessOutcome: "Recovers up to 40% of dormant abandoned carts and boosts digital ad ROI by engaging buyers instantly."
  },
  {
    id: "luxury-hospitality",
    name: "3. The Luxury Hospitality Concierge",
    tagline: "Five-Star Digital Guest Service on WhatsApp",
    aesthetic: "Impeccably refined, warm, and sophisticated. Employs a stunning color scheme of creams, champagne ivory, and satin brass with Playfair Display headings to evoke bespoke high-end luxury.",
    colors: {
      bg: "bg-[#FAF7F0]",
      text: "text-amber-950",
      border: "border-amber-900/10",
      accent: "amber",
      accentBg: "bg-[#C4A47C]",
      accentText: "text-amber-900",
      gradient: "from-[#FDFBF7] via-[#FAF7F0] to-[#F1ECE1]"
    },
    fontFamily: "serif",
    heroHeadlinePrefix: "Provide a flawless concierge for",
    typingWords: ["Guest Reservations.", "In-Room Requests.", "VIP Itineraries."],
    heroCTA: "Inquire About Digital Concierge",
    visualMockups: [
      {
        platform: "whatsapp",
        title: "Bespoke Resort Concierge",
        userQuery: "Hello, we are in Suite 402. Is it possible to arrange two deep-tissue massages at the spa at 4:30 PM, and have a bottle of Champagne sent up?",
        botResponse: "Good afternoon, Mrs. Sterling. I have reserved two therapists for you at 4:30 PM today. Our signature Champagne is also on its way to Suite 402 as we speak. Is there anything else I can perfect for your stay?",
        outcomeBadge: "Booked & Dispatched instantly",
        avatarUrl: "🥂",
        timestamp: "3:40 PM"
      }
    ],
    beforeVsAfter: {
      beforeLabel: "Friction-Ridden Lodging",
      afterLabel: "The Bespoke Lounge Experience",
      beforeIcon: "Clock",
      afterIcon: "Sparkles",
      beforeText: [
        "Guests waiting on hold for simple amenities like extra linens or dietary options.",
        "Overworked front desks juggling check-ins while ignoring inbound hospitality texts.",
        "Local excursion and dining partners manual booking bottlenecks."
      ],
      afterText: [
        "Inbound guest requests met with structured, ultra-polite replies in under 3 seconds.",
        "Automated room service, cleaning schedule adjusts, and billing reviews.",
        "Local luxury restaurant partnerships fully integrated for immediate automated bookings."
      ]
    },
    industryShowcase: [
      {
        industry: "Hospitality",
        subTitle: "Uncompromising Service Quality",
        workflowTitle: "Zero-Waiting Lobby Cycle",
        workflowSteps: [
          "Guest receives automated check-in prompt via WhatsApp pre-arrival",
          "ID uploaded safely, digital guest cards created automatically",
          "Custom check-in upgrades, dietary allergies mapped instantly",
          "Mobile-activated room key code pushed securely to guest's WhatsApp"
        ],
        valueMetric: "Reception wait times cut to 0 seconds",
        channelUsed: "Priority WhatsApp Concierge",
        highlightText: "Enable modern guests to bypass long front-desk lines entirely and access relaxation instantly."
      }
    ],
    emotionalHook: "Uphold the distinguished standards of your hospitality brand. Delight guests with bespoke, hyper-responsive attentiveness tailored to their every preference.",
    targetAudience: "Luxury Hotel Owners, Resort Operators, GM Concierges, and Hospitality Group Directors.",
    businessOutcome: "Boosts in-stay secondary guest spending (spa, dynamic room upgrade offers, dining) by active 24%."
  },
  {
    id: "fintech-security",
    name: "4. The Fintech Security & Assurance",
    tagline: "Bank-Grade Transaction Automation with Absolute Integrity",
    aesthetic: "Precise, corporate, high-security layout. Solid mathematical structure using cold metal-grey tiles, stark high contrast, and crisp emerald highlights for regulatory assurance.",
    colors: {
      bg: "bg-slate-50",
      text: "text-slate-900",
      border: "border-slate-300",
      accent: "emerald",
      accentBg: "bg-emerald-600",
      accentText: "text-emerald-700",
      gradient: "from-zinc-100/50 via-slate-50 to-emerald-50/20"
    },
    fontFamily: "display",
    heroHeadlinePrefix: "Automate financial support over",
    typingWords: ["Secure Web Chats.", "Encrypted SMS.", "WhatsApp Verification."],
    heroCTA: "Request Secure Integration",
    visualMockups: [
      {
        platform: "webchat",
        title: "Secure Portal Web Chat",
        userQuery: "I noticed a suspicious transaction of $420.50 on my credit card. Can I lock my account temporarily?",
        botResponse: "Verification complete. Your physical card ending in **4829 has been blocked successfully to prevent further access. I am generating an audit reference and sending it via secure SMS. Would you like me to guide you through card replacement?",
        outcomeBadge: "Secured & Blocked in 4.1 sec",
        avatarUrl: "🛡️",
        timestamp: "9:15 AM"
      }
    ],
    beforeVsAfter: {
      beforeLabel: "Anxious Security Vulnerabilities",
      afterLabel: "Auditable Financial Fortitude",
      beforeIcon: "ShieldAlert",
      afterIcon: "Lock",
      beforeText: [
        "Cardholder panic escalates when calling lines are closed during offline hours.",
        "Contractors accessing sensitive private databases to answer repetitive card locks.",
        "Manual error risks regarding legal compliance guidelines and policy misquotes."
      ],
      afterText: [
        "Instant responsive security buffers available 24/7/365 with high operational speed.",
        "Highly compartmentalized requests that resolve basic checks on-the-spot without exposing backend admin keys.",
        "Every single interaction logged and compliant with strict financial data governance standards."
      ]
    },
    industryShowcase: [
      {
        industry: "Fintech",
        subTitle: "Consumer Credit & Banking",
        workflowTitle: "SLA-Driven Micro-Loan Check",
        workflowSteps: [
          "Applicant triggers a qualification inquiry via secure, encrypted widget",
          "CAEL AI requests basic non-sensitive inputs and credit status files",
          "Calculates credit health indexes instantly using secure pipeline",
          "Presents tailored repayment pathways and updates banking logs safely"
        ],
        valueMetric: "SLA response time is immediate",
        channelUsed: "Secure Embedded SDK Webchat",
        highlightText: "Maximize customer acquisition speed while preserving rigorous identity protection and risk controls."
      }
    ],
    emotionalHook: "Provide premium-grade customer care that never compromises on safety, accuracy, or data protection. Maintain absolute integrity under high regulatory standards.",
    targetAudience: "Fintech Founders, Risk Management Leads, and Credit Union Compliance Executives.",
    businessOutcome: "Slashes active security reporting backlog by 88% while ensuring zero policy enforcement errors."
  },
  {
    id: "glassmorphic-bento",
    name: "5. The Glassmorphic Bento Box Benefits Grid",
    tagline: "Apple-Style UI Layout Exhibiting Omnichannel Dominance",
    aesthetic: "Modern high-growth startup aesthetic utilizing Apple-style floating translucent cards. Includes a beautiful bento grid of value propositions designed for visually sophisticated stakeholders.",
    colors: {
      bg: "bg-slate-50/40",
      text: "text-slate-900",
      border: "border-slate-200/90",
      accent: "teal",
      accentBg: "bg-teal-500",
      accentText: "text-teal-600",
      gradient: "from-[#F3F4F6] via-white to-[#EEF2F6]"
    },
    fontFamily: "sans",
    heroHeadlinePrefix: "A modern digital employee for",
    typingWords: ["Instant WhatsApp Workflows.", "Flawless Booking Control.", "Predictable Transaction Flows."],
    heroCTA: "Explore Interactive Bento Stack",
    visualMockups: [
      {
        platform: "whatsapp",
        title: "Omnichannel Floating Widget",
        userQuery: "Where is my order? Sizing was a bit off, can I exchange it?",
        botResponse: "I see order #10852 placed yesterday. I can issue a virtual replacement label. What size can I compile for you?",
        outcomeBadge: "Exchanged on WhatsApp",
        avatarUrl: "📦",
        timestamp: "Active Now"
      }
    ],
    beforeVsAfter: {
      beforeLabel: "Fragmented Standalone Tools",
      afterLabel: "The Unified Digital Employee",
      beforeIcon: "Server",
      afterIcon: "Layers",
      beforeText: [
        "Uncooperative manual tabs, chaotic channels, and lost conversations.",
        "Scattering support contexts across custom emails and lost post-its.",
        "Fragmented tools that require constant syncing and manual intervention."
      ],
      afterText: [
        "One central coordinate handling unified customer engagement flawlessly.",
        "Automatic system updates synchronized directly to your CRM.",
        "A cohesive multi-channel interface crafted in one secure workspace."
      ]
    },
    industryShowcase: [
      {
        industry: "Retail",
        subTitle: "E-Commerce Integration",
        workflowTitle: "Bento Box Flow Optimization",
        workflowSteps: [
          "Query arrives on Web Storefront chat widget",
          "Sizing profile matched within local cookies",
          "Automated recommendations pulled based on historical trends",
          "One-tap secure payment executed in high fidelity"
        ],
        valueMetric: "Unified experience rate is 100%",
        channelUsed: "Unified Platform Sync",
        highlightText: "A majestic, clean, modular array of benefits that showcases total multi-channel control on a single hub."
      }
    ],
    emotionalHook: "Bring stunning order to your customer communications. Replace ancient ticketing platforms with a unified digital employee that your customers will absolutely adore.",
    targetAudience: "VP of Product, Digital Transformation Executives, and modern Brand Founders.",
    businessOutcome: "Collapses multi-point platform billings to save up to $14,000 monthly in legacy support stack license fees."
  },
  {
    id: "customer-journey",
    name: "6. The Interactive Customer Journey",
    tagline: "Track the Conversion Flow from Chaos to Calm Resolution",
    aesthetic: "Intuitive chronological layout. Focuses heavily on the customer experience journey, contrasting an incoming user request with an immediate, stress-free automated milestone map.",
    colors: {
      bg: "bg-slate-50",
      text: "text-slate-900",
      border: "border-slate-200",
      accent: "sky",
      accentBg: "bg-sky-500",
      accentText: "text-sky-650",
      gradient: "from-sky-50 via-slate-50 to-blue-50/30"
    },
    fontFamily: "display",
    heroHeadlinePrefix: "Dramatically accelerate resolutions on",
    typingWords: ["Support DMs.", "Logistics Inquiries.", "Customer Requests."],
    heroCTA: "Analyze Customer Journey",
    visualMockups: [
      {
        platform: "instagram",
        title: "The Frictionless Journey",
        userQuery: "We've been trying to find out if you ship to Oslo, Norway? None of your competitors answer.",
        botResponse: "Yes, we ship to Oslo shipping-duty-paid! 🇳🇴 Standard transit takes 3 days, and shipping is complementary for orders over $150. Here is our direct localization menu for Nordic buyers.",
        outcomeBadge: "New Nordic Buyer Acquired",
        avatarUrl: "✈️",
        timestamp: "Active"
      }
    ],
    beforeVsAfter: {
      beforeLabel: "The Demoralizing Client Support Loop",
      afterLabel: "The Serene Autonomous Journey",
      beforeIcon: "Frown",
      afterIcon: "Smile",
      beforeText: [
        "Buyer is left hanging for 18 critical hours on Instagram DM during peak consideration.",
        "Anxious shopper receives generic, cold auto-replies saying 'we will respond in 24 hours'.",
        "Disappointed customer cancels order and purchases from an automated competitor."
      ],
      afterText: [
        "Immediate personalized attention triggered in under 500 milliseconds.",
        "Helpful answers, custom catalogs, and sizing insights presented directly inside the DM.",
        "Purchase finalized effortlessly, producing high loyalty and immediate five-star online reviews."
      ]
    },
    industryShowcase: [
      {
        industry: "Support",
        subTitle: "High-Touch Support Journey",
        workflowTitle: "Milestone-Driven Resolution",
        workflowSteps: [
          "Frustrated user requests complex transaction breakdown on mobile Web Chat",
          "CAEL AI acknowledges query instantly with custom polite empathy styling",
          "Gathers previous account history from safe read-only cache secure systems",
          "Presents precise line items and resolves inquiry without calling phone support"
        ],
        valueMetric: "Average hold time cut to 0 seconds",
        channelUsed: "Dynamic Web Chat Component",
        highlightText: "Witness a customer flow transformed from immediate friction to structured, rapid satisfaction."
      }
    ],
    emotionalHook: "Deliver a remarkable customer experience that keeps buyers coming back. Turn every initial doubt into an immediate high-loyalty relationship.",
    targetAudience: "Director of Customer Care, Customer Experience Leads, and Brand Strategists.",
    businessOutcome: "Boosts Net Promoter Score (NPS) to top-tier 85+ while dropping support tickets by 50%."
  },
  {
    id: "roi-calculator",
    name: "7. The ROI Calculator First Layout",
    tagline: "Calculate Your Monthly Operational Savings Instantly",
    aesthetic: "Highly operational and practical design centering an interactive sliders model. Provides business stakeholders with dynamic, visual proof of financial returns and staffing cost reductions.",
    colors: {
      bg: "bg-slate-50/60",
      text: "text-slate-900",
      border: "border-slate-200",
      accent: "teal",
      accentBg: "bg-teal-600",
      accentText: "text-teal-700",
      gradient: "from-teal-50/30 via-slate-50 to-emerald-50/20"
    },
    fontFamily: "sans",
    heroHeadlinePrefix: "Calculate massive savings on",
    typingWords: ["Support Agents.", "Operational Costs.", "Abandoned Checkout Carts."],
    heroCTA: "Lock In Your Projected Savings",
    visualMockups: [
      {
        platform: "webchat",
        title: "ROI Validation Chat Engine",
        userQuery: "We handle 12,000 inquiries a month. How much does our cost-per-ticket drops with CAEL AI?",
        botResponse: "Based on our micro-audit metrics, your current manual cost-per-ticket of $5.50 collapses to just $0.45 per automated resolution. That equates to $60,600 saved every single month.",
        outcomeBadge: "Operational cost cut by 91%",
        avatarUrl: "📊",
        timestamp: "Calculated"
      }
    ],
    beforeVsAfter: {
      beforeLabel: "The Exploding Operational Balance Sheet",
      afterLabel: "The Highly Optimized Workforce",
      beforeIcon: "TrendingDown",
      afterIcon: "DollarSign",
      beforeText: [
        "High average support costs (over $6 per ticket) driven by low agent throughput.",
        "Excessive recruiting and training costs caused by high call center agent turnover.",
        "Revenue loss as support teams miss late-night customer inquiries."
      ],
      afterText: [
        "Immediate support costs slashed to under $0.50 per automated ticket.",
        "Zero hiring backlogs—support capacity scales up or down instantly with demand.",
        "Captures 100% of international or late-night inquiries, unlocking idle revenue."
      ]
    },
    industryShowcase: [
      {
        industry: "Support",
        subTitle: "Financial Optimization Audit",
        workflowTitle: "Staff Allocation Blueprint",
        workflowSteps: [
          "Assess incoming monthly support traffic volume benchmarks",
          "Identify basic FAQs and transaction workflows to outsource to CAEL AI",
          "Auto-resolve 85% of tier-1 support queries instantly",
          "Redirect senior personnel to premium enterprise partnerships"
        ],
        valueMetric: "Average cost-per-ticket dropped by 91%",
        channelUsed: "Interactive Optimization Hub",
        highlightText: "Witness how direct digital automation delivers powerful, predictable savings directly back to your bottom line."
      }
    ],
    emotionalHook: "Why bleed capital on repetitive questions? Leverage a predictable, secure AI workforce that pays for itself on day one and scales infinitely.",
    targetAudience: "Chief Financial Officers, Directors of Operations, and Retail Founders.",
    businessOutcome: "Provides a verifiable, immediate ROI of up to 10x your initial design and pipeline investment."
  },
  {
    id: "social-proof",
    name: "8. The Social Proof & Case Study Hero",
    tagline: "Proven Success and High Praise from Industry Leaders",
    aesthetic: "Authority-building, clean, and media-rich. Framed by crisp testimonials, actual performance statistics, and verified business outcomes from major pilot partnerships.",
    colors: {
      bg: "bg-white",
      text: "text-slate-900",
      border: "border-slate-200/90",
      accent: "rose",
      accentBg: "bg-rose-500",
      accentText: "text-rose-600",
      gradient: "from-rose-50/10 via-white to-orange-50/10"
    },
    fontFamily: "outfit",
    heroHeadlinePrefix: "Deliver exceptional outcomes across",
    typingWords: ["Hospitality Portals.", "E-Commerce Stores.", "Fintech Systems."],
    heroCTA: "Read Our Complete Case Study",
    visualMockups: [
      {
        platform: "whatsapp",
        title: "Bespoke Resort Audit",
        userQuery: "We've been using CAEL AI for a month. Our staff says the front-desk call load decreased dramatically?",
        botResponse: "Our database confirms front-desk call volume decreased by an stellar 73% while overall guest rating reviews surged. Let's look at the next workflow optimization.",
        outcomeBadge: "Verified Pilot Outcomes",
        avatarUrl: "🏖️",
        timestamp: "Audit Run"
      }
    ],
    beforeVsAfter: {
      beforeLabel: "Skeptical Corporate Inefficiencies",
      afterLabel: "The Verified High-Success System",
      beforeIcon: "ThumbsDown",
      afterIcon: "Award",
      beforeText: [
        "Paying high consulting fees to build custom support systems that fail to deliver results.",
        "Fear that customers will reject automated conversational tools.",
        "Vague, unproven marketing promises from untrusted AI tools."
      ],
      afterText: [
        "A proven, plug-and-play workflow integration that delivers clear results within 48 hours.",
        "Customers highly praise the instant, helpful response quality—NPS spikes to 86.",
        "Fully transparent, highly auditable performance metrics and outcomes."
      ]
    },
    industryShowcase: [
      {
        industry: "Hospitality",
        subTitle: "Proven Hospitality Results",
        workflowTitle: "The Grand Resort Case Study",
        workflowSteps: [
          "Implemented CAEL AI across 4 luxury resort properties",
          "Automated 14,000 guest requests with average speed of 2.1 seconds",
          "Achieved 96% guest satisfaction ratings on automated excursions",
          "Front desk teams freed to focus on high-priority in-person check-ins"
        ],
        valueMetric: "Room service orders up by 31%",
        channelUsed: "WhatsApp Hospitality Hub",
        highlightText: "Witness how premium hospitality leaders turn support into active luxury experiences."
      }
    ],
    emotionalHook: "Join top e-commerce, hospitality, and fintech brands that trust CAEL AI to manage their customer interactions with absolute precision and elegance.",
    targetAudience: "VPs of Service, Board Members, and Growth-focused Franchise Owners.",
    businessOutcome: "Guarantees a verified CSAT score of over 92% from your very first week of launch."
  },
  {
    id: "apple-minimalist",
    name: "9. The Futuristic Apple-Style Minimalist",
    tagline: "The Future of Customer Engagement, Simplified Beyond Measure",
    aesthetic: "Immersive off-white and soft metallic silver aesthetics. Emphasizes clean typography, extensive whitespace, and beautiful floating panels to convey simple modern luxury.",
    colors: {
      bg: "bg-[#FAFAFA]",
      text: "text-zinc-900",
      border: "border-zinc-200/80",
      accent: "zinc",
      accentBg: "bg-zinc-800",
      accentText: "text-zinc-600",
      gradient: "from-zinc-100 via-neutral-50 to-zinc-50"
    },
    fontFamily: "outfit",
    heroHeadlinePrefix: "Frictionless automated chat on",
    typingWords: ["WhatsApp.", "Instagram.", "Web Chat."],
    heroCTA: "Experience Elegant Automation",
    visualMockups: [
      {
        platform: "webchat",
        title: "The Pristine Interface",
        userQuery: "I want an assistant that handles booking, support, and sales seamlessly. Can CAEL do that?",
        botResponse: "Yes, perfectly. I integrate directly into your existing channels to handle reservations, process transaction returns, and guide purchase checkouts natively.",
        outcomeBadge: "Friction Completely Eliminated",
        avatarUrl: "✴️",
        timestamp: "Live"
      }
    ],
    beforeVsAfter: {
      beforeLabel: "The Bloated Legacy Support Queue",
      afterLabel: "The Elegant Digital Employee",
      beforeIcon: "Compass",
      afterIcon: "Zap",
      beforeText: [
        "Bloated support structures dragging down operational agility.",
        "Cluttered dashboards, confusing tools, and endless trainee manuals.",
        "Disconnected team channels communicating conflicting business policies."
      ],
      afterText: [
        "A singular, beautiful digital employee that requires zero management overhead.",
        "Stunning, high-end design that matches luxury and premium brands.",
        "Perfect compliance with your brand voice and operational guidelines."
      ]
    },
    industryShowcase: [
      {
        industry: "Support",
        subTitle: "Pure Aesthetic Mastery",
        workflowTitle: "Frictionless Interaction Setup",
        workflowSteps: [
          "Connect active support channels with one-tap configuration",
          "Ingest business rules and product catalogs with high precision",
          "CAEL AI begins active customer engagement in real-time",
          "Delivers clean performance insights directly to your executive inbox"
        ],
        valueMetric: "Saves 180 leadership hours/month",
        channelUsed: "Pristine CAEL Portal",
        highlightText: "Achieve ultimate simplicity. Settle for nothing less than a perfectly executed customer experience."
      }
    ],
    emotionalHook: "Settle for nothing less than absolute visual and operational perfection. Introduce your business to the most elegant customer care platform ever designed.",
    targetAudience: "Design-minded Founders, Chief Creative Officers, and Elite Brand Directors.",
    businessOutcome: "Reduces user onboarding friction to zero while elevating customer message engagement by 60%."
  },
  {
    id: "omnichannel-preview",
    name: "10. The Live Omni-Channel Preview Concept",
    tagline: "See CAEL AI Drive Conversions Across WhatsApp and DMs Concurrently",
    aesthetic: "Dynamic, split-screen concept. One half delivers persuasive executive messaging, while the other features interactive, simultaneous chat simulations across multiple popular networks.",
    colors: {
      bg: "bg-slate-50",
      text: "text-zinc-900",
      border: "border-slate-300",
      accent: "violet",
      accentBg: "bg-violet-600",
      accentText: "text-violet-700",
      gradient: "from-violet-50/20 via-zinc-100/50 to-indigo-50/20"
    },
    fontFamily: "display",
    heroHeadlinePrefix: "Unify your customer engagement via",
    typingWords: ["WhatsApp Commerce.", "Instagram DM Lead Capture.", "Live Web Concierge."],
    heroCTA: "Activate Omni-Channel Matrix",
    visualMockups: [
      {
        platform: "whatsapp",
        title: "WhatsApp Core Integration",
        userQuery: "Do you have any boutique rooms left for tonight in downtown?",
        botResponse: "Yes, Mrs. Hayes. Our last Executive Suite is available for $280/night. Tap below to finalize this reservation and receive your digital key.",
        outcomeBadge: "Reserved on WhatsApp",
        avatarUrl: "🟢",
        timestamp: "Active"
      },
      {
        platform: "instagram",
        title: "Instagram DM Lead Capture",
        userQuery: "Hello, I commented on your suit post, is size 40R still in stock?",
        botResponse: "We have only 2 left in 40R! 👔 I can generate a purchase link with overnight shipping ready for you. Would you like to proceed?",
        outcomeBadge: "Checkout link generated in DM",
        avatarUrl: "🟣",
        timestamp: "Active"
      }
    ],
    beforeVsAfter: {
      beforeLabel: "The Siloed Inboxes Hell",
      afterLabel: "The Unified Omni-Channel Matrix",
      beforeIcon: "Shuffle",
      afterIcon: "Grid",
      beforeText: [
        "Inquiries dropping because teams check WhatsApp but miss Instagram DMs.",
        "Fragmented customer stories with zero unified profile data across apps.",
        "Slow response times as agents jump between different tabs and channels."
      ],
      afterText: [
        "Every single customer query met instantly, regardless of the platform used.",
        "A unified customer history profile updated automatically in real-time.",
        "Saves time and eliminates friction with a single cohesive digital worker."
      ]
    },
    industryShowcase: [
      {
        industry: "Support",
        subTitle: "Continuous Customer Engagement",
        workflowTitle: "Omni-Channel Lead Routing",
        workflowSteps: [
          "Customer initiates an inquiry on Instagram DM",
          "CAEL AI addresses query and requests a contact number",
          "Instantly opens a secure WhatsApp thread to finalize reservation details",
          "Logistics updates pushed automatically to keep the shopper informed"
        ],
        valueMetric: "Zero leads lost to channel friction",
        channelUsed: "Cross-Channel Synchronization",
        highlightText: "Witness the sheer power of an automated system that handles sales and support across all platforms at once."
      }
    ],
    emotionalHook: "Never lose a valuable customer to channel complexity. Meet your buyers exactly where they are with absolute consistency and speed.",
    targetAudience: "Chief Revenue Officers, Multi-brand Retail Groups, and Omnichannel Marketers.",
    businessOutcome: "Collapses multi-platform lead loss to zero and elevates message response speed by 99%."
  }
];
